// OtaHeaderChip — a compact version/update control that lives in the Hayah
// header, so it's reachable from any Hayah screen. It always shows the version
// you're running, and tells you at a glance whether the phone is on the latest
// build. Tap it to force a check + update right now (no waiting on launch
// timing). Native-only — renders nothing on web/PWA.

import { useCallback, useEffect, useState } from "react";
import { getOtaStatus, checkForOtaUpdate } from "../../otaUpdater";

function isNative() {
  try { return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()); }
  catch { return false; }
}
function shortVer(v) {
  if (!v || v === "builtin") return "built-in";
  return String(v).slice(0, 7);
}

export default function OtaHeaderChip({ darkMode = true }) {
  const [state, setState] = useState("checking"); // checking|uptodate|available|updating|offline
  const [cur, setCur] = useState(null);
  const [latest, setLatest] = useState(null);
  const [flash, setFlash] = useState(null); // transient label after a tap

  const refresh = useCallback(async () => {
    setState("checking");
    const s = await getOtaStatus();
    if (!s) { setState("uptodate"); return; }
    setCur(s.current); setLatest(s.latest);
    setState(s.latest == null ? "offline" : (s.upToDate ? "uptodate" : "available"));
  }, []);

  useEffect(() => { if (isNative()) refresh(); }, [refresh]);

  const onTap = useCallback(async () => {
    if (state === "updating" || state === "checking") return;
    try { import("../../hayahHaptics").then(m => m.hapticTap && m.hapticTap()).catch(() => {}); } catch {}
    setState("checking");
    const s = await getOtaStatus();
    if (s) { setCur(s.current); setLatest(s.latest); }
    if (s && !s.upToDate && s.latest != null) {
      setState("updating");
      const r = await checkForOtaUpdate({ force: true });
      // On success the webview reloads (set()), so this rarely runs.
      if (r !== "updating") {
        setState(r === "uptodate" ? "uptodate" : (r === "offline" ? "offline" : "available"));
      }
    } else {
      setState("uptodate");
      setFlash("You're on the latest");
      setTimeout(() => setFlash(null), 1900);
    }
  }, [state]);

  if (!isNative()) return null;

  const green = "#00c9a7";
  const amber = "#ffb300";
  const muted = darkMode ? "#5a7387" : "#5a6f80";
  const mono = "'Geist Mono', ui-monospace, 'SF Mono', Menlo, monospace";

  let label, fg, bg, bd, dot = false, spin = false;
  if (flash) {
    label = flash; fg = green; bg = "rgba(0,201,167,0.12)"; bd = "rgba(0,201,167,0.4)";
  } else if (state === "checking") {
    label = "Checking"; fg = muted; bg = "transparent"; bd = darkMode ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"; spin = true;
  } else if (state === "updating") {
    label = "Updating…"; fg = green; bg = "rgba(0,201,167,0.12)"; bd = "rgba(0,201,167,0.4)"; spin = true;
  } else if (state === "available") {
    label = "Update"; fg = "#03110a"; bg = green; bd = green; dot = true;
  } else if (state === "offline") {
    label = `v ${shortVer(cur)}`; fg = muted; bg = "transparent"; bd = darkMode ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)";
  } else { // uptodate
    label = `v ${shortVer(cur)}`; fg = muted; bg = "transparent"; bd = darkMode ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)";
  }

  return (
    <button
      onClick={onTap}
      aria-label={state === "available" ? "Update available — tap to update" : "App version — tap to check for updates"}
      title={latest && cur && latest !== cur ? `On v${shortVer(cur)} · latest v${shortVer(latest)}` : `On v${shortVer(cur)}`}
      style={{
        flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 5,
        padding: "5px 9px", borderRadius: 999, cursor: "pointer",
        background: bg, border: `0.5px solid ${bd}`, color: fg,
        fontFamily: mono, fontSize: 9, fontWeight: 700, letterSpacing: "0.6px",
        textTransform: "uppercase", WebkitTapHighlightColor: "transparent",
        lineHeight: 1, maxWidth: 160, whiteSpace: "nowrap", overflow: "hidden",
      }}>
      <style>{`@keyframes ota-spin{to{transform:rotate(360deg)}}@keyframes ota-pulse{0%,100%{opacity:1}50%{opacity:0.35}}`}</style>
      {state === "uptodate" && !flash && (
        <span style={{ color: green, fontSize: 10, lineHeight: 1 }}>✓</span>
      )}
      {spin && (
        <span style={{ width: 9, height: 9, borderRadius: "50%", border: `1.4px solid ${fg}`, borderTopColor: "transparent", display: "inline-block", animation: "ota-spin 0.7s linear infinite" }} />
      )}
      {dot && (
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#03110a", display: "inline-block", animation: "ota-pulse 1.4s ease-in-out infinite" }} />
      )}
      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
    </button>
  );
}
